import { Button, Flex, message, Select, Switch, Table } from 'antd';
import { EyeOutlined, LeftOutlined, RightOutlined } from '@ant-design/icons';
import { useCallback, useEffect, useMemo, useState } from 'react';
import getSubscriptionCosts from '../../../domain/use_cases/costs/getSubscriptionCosts';
import { ICost } from '@leedpulse-monorepo/interfaces';
import { costsColumns } from '../@shared/costs/costsColumns';
import { creditsCycleAt } from '@leedpulse-monorepo/utils';
import { FirestoreCollections } from '@leedpulse-monorepo/firebase-metadata';
import RawDocumentModal from '../@shared/RawDocumentModal';

const FETCH_LIMIT = 100;
const DEFAULT_PAGE_SIZE = 10;
const MAX_CYCLES = 60; // safety cap

interface SubscriptionCostProps {
  subscriptionId: string;
  currentLeedbucks: number;
  totalLeedbucks: number;
  planStart?: number;
  planEnd?: number;
  creditsPeriodEnd?: number;
}

interface Cycle {
  start: number;
  end: number;
  is_current: boolean;
}

// Monthly credits cycles, walked back to the oldest cost (minDate). Monthly whatever the
// billing interval: an annual subscription is shown as twelve cycles, not as one period.
const buildCycles = (anchor: number, minDate: number): Cycle[] => {
  if (!anchor) return [];
  const now = Date.now();
  const cycles: Cycle[] = [];
  let cursor = now;
  for (let i = 0; i < MAX_CYCLES; i++) {
    const cycle = creditsCycleAt(anchor, cursor);
    cycles.push({ start: cycle.start, end: cycle.end, is_current: now >= cycle.start && now < cycle.end });
    if (cycle.start <= minDate) break;
    cursor = cycle.start - 1; // Step into the previous window
  }
  return cycles.reverse();
};

const formatDate = (ms: number): string =>
  new Date(ms).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });

/** Cycle courant, ou le dernier a defaut. -1 quand il n'y a aucun cycle. */
const pickCurrentCycleIndex = (list: { is_current?: boolean }[]): number => {
  if (list.length === 0) return -1;
  const idx = list.findIndex((c) => c.is_current);
  return idx >= 0 ? idx : list.length - 1;
};

const SubscriptionCost: React.FC<SubscriptionCostProps> = ({ subscriptionId, planEnd, creditsPeriodEnd }) => {
  const [costs, setCosts] = useState<ICost[]>([]);
  const [consumed, setConsumed] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [onlyCredits, setOnlyCredits] = useState<boolean>(false);
  const [oldestDate, setOldestDate] = useState<number | null>(null);
  const [inspectedCost, setInspectedCost] = useState<ICost | null>(null);

  // The credits cycle end is a real cycle boundary; plan_end is not (it carries a month of
  // grace), so it only serves as a fallback before the cycle is resolved.
  const anchor = creditsPeriodEnd ?? planEnd ?? 0;
  const cycles = useMemo(() => buildCycles(anchor, oldestDate ?? anchor), [anchor, oldestDate]);
  // L'index DOIT etre calcule des le premier rendu, pas seulement au changement
  // de `cycles` : le pane antd de cet onglet monte au clic, donc `selected` est
  // deja charge et `buildCycles` rend deja un cycle. Avec `useState(-1)`, aucun
  // cycle n'etait selectionne, `getCosts` sortait tot, `oldestDate` n'etait jamais
  // pose, `cycles` ne changeait donc jamais et `showLoader` restait vrai a vie.
  const [cycleIndex, setCycleIndex] = useState<number>(() => pickCurrentCycleIndex(cycles));

  const [lastCycles, setLastCycles] = useState(cycles);
  if (cycles !== lastCycles) {
    setLastCycles(cycles);
    setCycleIndex(pickCurrentCycleIndex(cycles));
  }

  const selectedCycle = cycleIndex >= 0 ? cycles[cycleIndex] ?? null : null;

  const getCosts = useCallback(async () => {
    if (!selectedCycle) return undefined;
    try {
      setLoading(true);
      return await getSubscriptionCosts(subscriptionId, {
        limit: FETCH_LIMIT,
        onlyCredits,
        start: selectedCycle.start,
        end: selectedCycle.end,
      });
    } catch (error) {
      message.error((error as Error).message);
    } finally {
      setLoading(false);
    }
  }, [subscriptionId, onlyCredits, selectedCycle?.start, selectedCycle?.end]);

  useEffect(() => {
    // Synchro avec une source distante : l'etat vient d'un appel reseau, pas du
    // rendu. C'est l'usage d'un effet que la regle autorise ("synchronizing with an
    // external system") ; le setState synchrone est le reset qui precede l'appel.
    // eslint-disable-next-line react/set-state-in-effect
    getCosts().then((result) => {
      if (!result) return;
      setCosts(result.costs ?? []);
      setConsumed(result.consumed ?? 0);
      setOldestDate(result.oldest ?? null);
    });
  }, [getCosts]);

  const columns = [
    ...costsColumns.map((column) =>
      column.key === 'created'
        ? { ...column, sorter: (a: ICost, b: ICost) => a.created - b.created, defaultSortOrder: 'descend' as const }
        : column
    ),
    {
      key: 'inspect',
      width: 48,
      render: (_: unknown, record: ICost) => (
        <Button
          type="text"
          size="small"
          icon={<EyeOutlined />}
          onClick={(e) => {
            // The row click copies the id
            e.stopPropagation();
            setInspectedCost(record);
          }}
        />
      ),
    },
  ];

  const showLoader = loading || (cycles.length > 0 && !selectedCycle);

  return (
    <Flex vertical gap={12}>
      <Flex align="center" justify="space-between" gap={12} wrap="wrap">
        <Flex align="center" gap={12}>
          <span style={{ fontSize: '16px', fontWeight: 600 }}>{consumed} crédits consommés</span>
          {selectedCycle && (
            <Flex align="center" gap={4}>
              <Button
                type="text"
                icon={<LeftOutlined />}
                disabled={cycleIndex <= 0}
                onClick={() => setCycleIndex((i) => Math.max(0, i - 1))}
              />
              <Select
                value={cycleIndex}
                onChange={(value) => setCycleIndex(value)}
                options={cycles
                  .map((c, i) => ({ value: i, label: `${formatDate(c.start)} - ${formatDate(c.end)}` }))
                  .reverse()}
                style={{ minWidth: 280 }}
              />
              <Button
                type="text"
                icon={<RightOutlined />}
                disabled={cycleIndex >= cycles.length - 1}
                onClick={() => setCycleIndex((i) => Math.min(cycles.length - 1, i + 1))}
              />
            </Flex>
          )}
        </Flex>
        <Flex align="center" gap={8}>
          <span>Uniquement les coûts liés aux crédits</span>
          <Switch checked={onlyCredits} onChange={setOnlyCredits} />
        </Flex>
      </Flex>

      <Table
        pagination={{ pageSize: DEFAULT_PAGE_SIZE, showSizeChanger: true }}
        loading={showLoader}
        columns={columns}
        rowKey={(record) => record.id}
        dataSource={costs}
        onRow={(record) => ({
          onClick: async (e) => {
            e.stopPropagation();
            try {
              await navigator.clipboard.writeText(record.id);
              message.success('ID copié dans le presse-papiers');
            } catch {
              message.error('Copie impossible (presse-papiers indisponible)');
            }
          },
        })}
      />

      <RawDocumentModal
        collection={FirestoreCollections.costs}
        documentId={inspectedCost?.id ?? null}
        loadedDocument={inspectedCost as Record<string, unknown> | null}
        onClose={() => setInspectedCost(null)}
      />
    </Flex>
  );
};

export default SubscriptionCost;
